import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Upload } from "lucide-react";
import { useRef, useState } from "react";
import { uploadFile } from "../_utils/uploadFile";

interface UploadFilePopupProps {
  files: File[];
  setFiles: (files: File[]) => void;
  uploadStatus: Record<string, number>; // Maps file names to upload status (0: pending, 30: validating, 60: uploading, -1: error)
  setUploadStatus: (status: Record<string, number>) => void;
  tempId?: string; // Temporary ID for the upload context
}
export function UploadFilePopup({
  files,
  setFiles,
  uploadStatus,
  setUploadStatus,
  tempId,
}: UploadFilePopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragOver(true);
    } else if (e.type === "dragleave") {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const newFiles = Array.from(e.dataTransfer.files);
      uploadFile({
        newFiles,
        files,
        setFiles,
        uploadStatus,
        setUploadStatus,
        tempId,
      });
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      uploadFile({
        newFiles,
        files,
        setFiles,
        uploadStatus,
        setUploadStatus,
        tempId,
      });
      e.target.value = ""; // Reset the input value to allow re-uploading the same file
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const openFileExplorer = () => {
    fileInputRef.current?.click();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button className="gap-2" size={"sm"}>
          <Upload className="h-4 w-4" />
          Upload Files
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="center" side="top">
        <div className="space-y-3">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={cn(
              "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50"
            )}
          >
            <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <div className="text-sm text-muted-foreground p-2">
              Click to upload or drag and drop
            </div>
            <div className="text-xs text-muted-foreground p-2">
              PDF, JPG, PNG up to 15MB each
            </div>
            <Button variant="outline" size="sm" onClick={openFileExplorer}>
              Browse Files
            </Button>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInput}
            accept=".pdf,.jpg,.jpeg,.png"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
